import { EntityTitle } from '@app/icare/components/entity/EntityTitle'
import { PatientSeparateResidenceEditDrawer } from '@app/icare/entities/patient/edits/PatientSeparateResidenceEditDrawer'
import {
  patientFindingsQueryOptions,
  selectByGroupAndGroupByRelationId,
} from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import { patientSeparateResidenceQueries } from '@app/icare/entities/patient/queries/patientSeparateResidenceQueries'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import {
  PatientSeparateResidenceDiscriminatorEnum,
  type PatientSeparateResidenceRelation,
} from '@app/icare/entities/patient/schema/PatientSeparateResidence'
import { RelationOverview } from '@app/icare/routes/_app/patienten/$id/_edit/-components/RelationOverview'
import { isActivePatientRelation } from '@app/icare/shared/api'
import { t } from '@lingui/core/macro'
import { entityParams } from '@pkg/db/utils'
import { formatAddress } from '@pkg/shared/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useRelationCommitNavigation } from '../-components/useRelationCommitNavigation'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_residences/aufenthaltsorte/')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    queryClient.ensureQueryData(patientSeparateResidenceQueries.forIndex(params.id)),
  component: RouteComponent,
})

function RouteComponent() {
  const { id } = Route.useParams()
  const { data: residences = [] } = useSuspenseQuery(patientSeparateResidenceQueries.forIndex(id))
  const { data: findings = {} } = useQuery({
    ...patientFindingsQueryOptions(id),
    select: selectByGroupAndGroupByRelationId('separateResidences'),
  })

  const labels = { label: t`Aufenthaltsort`, labelPlural: t`Aufenthaltsorte` }
  const navigate = Route.useNavigate()

  const handleAfterCommit = useRelationCommitNavigation<PatientSeparateResidenceRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: relation =>
      navigate({
        to: '/patienten/$id/aufenthaltsorte/$relation',
        params: { id, relation: relation.id },
      }),
    onInactiveEditNavigate: () => navigate({ to: '.', replace: true }),
  })

  return (
    <RelationOverview
      items={residences}
      labels={labels}
      findings={findings}
      basePath={`/patienten/${id}/aufenthaltsorte`}
      getLabel={item =>
        item.type === PatientSeparateResidenceDiscriminatorEnum.patientAddress ? (
          formatAddress([item.firstName, item.lastName], item.address) || t`Urlaubsort`
        ) : (
          <EntityTitle id={item.toId} />
        )
      }
      onEdit={params =>
        void PatientSeparateResidenceEditDrawer.show({ patientId: id, onAfterCommit: handleAfterCommit, ...params })
      }
    />
  )
}
